// Small helpers for formatting VACanada data.

/** Seconds → "234h 12m" */
export const fmtHours = (secs) => {
  if (secs == null) return "—";
  const s = Number(secs);
  if (!isFinite(s)) return "—";
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return `${h.toLocaleString()}h ${String(m).padStart(2, "0")}m`;
};

/** Unix epoch seconds → local "HH:MM UTC" */
export const fmtZulu = (secs) => {
  if (!secs) return "—";
  const d = new Date(Number(secs) * 1000);
  const h = String(d.getUTCHours()).padStart(2, "0");
  const m = String(d.getUTCMinutes()).padStart(2, "0");
  return `${h}:${m}z`;
};

export const fmtDate = (secs) => {
  if (!secs) return "—";
  const d = new Date(Number(secs) * 1000);
  return d.toISOString().slice(0, 10);
};

/**
 * A bid's scheduled times, as myvac stores them — both LOCAL to their airport.
 *
 * `date` is the departure as a local wall-clock time written as if it were UTC
 * (pairing.php adds the departure airport's offset before storing it), which
 * is why myvac reads it with gmdate() and labels it "L". `arrtime` is a local
 * "HHMM" string. Neither is Zulu, and the Dashboard says so.
 */
export const bidDepLocal = (date) => {
  if (!date) return "—";
  const d = new Date(Number(date) * 1000);
  if (Number.isNaN(d.getTime())) return "—";
  return `${String(d.getUTCHours()).padStart(2, "0")}:${String(d.getUTCMinutes()).padStart(2, "0")}`;
};

export const bidArrLocal = (arrtime) => {
  const m = /^\s*(\d{1,2}):?(\d{2})\s*$/.exec(String(arrtime ?? ""));
  return m ? `${m[1].padStart(2, "0")}:${m[2]}` : "—";
};

/** seconds-from-midnight (schedule.deptime) → HH:MM */
export const fmtSchedTime = (v) => {
  if (v == null) return "—";
  const s = Number(v);
  // Some values look like 47100 (seconds), some like 300 (minutes-ish). Assume seconds mod 86400.
  const total = s % 86400;
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
};

/** Compute great-circle distance in NM (simple haversine) */
export const distNm = (lat1, lon1, lat2, lon2) => {
  const toRad = (d) => (d * Math.PI) / 180;
  const R = 3440.065; // NM
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
};

/**
 * Compute the current flight phase from telemetry + OOOI times.
 *
 * `t` — telemetry snapshot: { alt, spd, vs, on_ground }
 * `ctx` — active-flight OOOI state: { time_out, time_off, time_on, time_in }
 *
 * Phases ratchet forward via the OOOI events so a stationary aircraft at a
 * high-elevation airport (e.g. CYYC 3557ft) doesn't jump to CRUISE, and a
 * landed aircraft never gets stuck on CRUISE while waiting for `time_on` to
 * propagate from the backend.
 *
 * `lastPhase` (optional) gives us hysteresis: we only enter CLIMB/DESCENT
 * on strong VS, but we don't leave until VS decays past a smaller threshold.
 * That kills the CLIMB↔CRUISE flapping around 500 fpm.
 *
 * Once descending, a level-off is still the descent. Arrivals step down —
 * level at FL240 for a crossing restriction, at 10,000 ft to slow to 250 kt —
 * and each of those used to read as CRUISE and play the cruise announcement
 * mid-descent (reported 2026-10-04). Only a real climb (a go-around, or ATC
 * sending the aircraft back up) leaves DESCENT or APPROACH.
 *
 * The climb has the same problem the other way: ATC levels departures off
 * below their cruise level (FL230 on the way to FL310, beta report
 * 2026-10-07) and each level-off read as CRUISE. So the climb ends only at the
 * cruise level: within CRUISE_BAND_FT of the flight plan's (`ctx.cruise_ft`,
 * from the OFP), or after holding level CAPPED_LEVEL_S when ATC never clears
 * the aircraft that high. Without a flight plan, after NO_PLAN_LEVEL_S level.
 * `ctx.level_secs` is how long the aircraft has been level; FlightRecorder
 * keeps it.
 *
 * And a descent far from the destination is a step down in the cruise, not
 * the descent (beta report 2026-10-15: ATC sent a flight 5,000 ft lower for
 * traffic mid-flight; it read DESCENT for the rest of the flight and the
 * descent announcement played hours early). A real descent starts within
 * about 3 nm per 1,000 ft of height plus a margin for arrival restrictions:
 * `descentReachNm`. Further out than that (`ctx.dist_to_go_nm`, great-circle
 * to the destination), descending or level, the flight is still CRUISE. With
 * no distance known, as before.
 */
export const CRUISE_BAND_FT = 1000;
export const DESCENT_MARGIN_NM = 50;
/** How far out a real descent from this altitude can begin, in nm. */
export const descentReachNm = (alt) => 3 * (Math.max(0, Number(alt) || 0) / 1000) + DESCENT_MARGIN_NM;
export const CAPPED_LEVEL_S = 600;
export const NO_PLAN_LEVEL_S = 300;

/** Has a level-off coming out of the climb reached cruise? */
export const atCruise = (alt, ctx = {}) => {
  const level = Number(ctx.level_secs) || 0;
  const planned = Number(ctx.cruise_ft) || 0;
  if (planned > 0) return alt >= planned - CRUISE_BAND_FT || level >= CAPPED_LEVEL_S;
  return level >= NO_PLAN_LEVEL_S;
};

export const phaseFromTelemetry = (t = {}, ctx = {}, lastPhase = null) => {
  const { alt = 0, spd = 0, vs = 0, on_ground } = t;
  // Unknown is NOT a guess from speed and altitude. The guess was "on the
  // ground below 3 kt and 300 ft MSL", which every pushback (over 3 kt) and
  // every airport above 300 ft (MMMY is 1,270) fails — and a page shows its
  // first frame before the sim's flag arrives, so opening Live Tracking during
  // pushback logged CLIMB (reported 2026-10-04).
  const onGnd = on_ground === undefined || on_ground === null ? null : !!on_ground;
  const time_out = Number(ctx.time_out || 0);
  const time_off = Number(ctx.time_off || 0);
  const time_on  = Number(ctx.time_on  || 0);
  const time_in  = Number(ctx.time_in  || 0);

  if (time_in) return "ARRIVED";
  if (time_on) return "TAXI IN";

  // Post-takeoff, pre-touchdown. Ground state check FIRST — if the wheels
  // touch and the server hasn't fired `time_on` yet, don't linger on CRUISE.
  if (time_off) {
    if (onGnd === true) return "TAXI IN";

    // Hysteresis: enter CLIMB at +500, only leave when VS drops below +200.
    //             enter DESCENT at -500, leave only by climbing at +500.
    const enterClimb   = vs > 500;
    const stayClimb    = vs > 200;
    const enterDescent = vs < -500;
    const toGo = ctx.dist_to_go_nm;
    const farOut = toGo !== undefined && toGo !== null && Number.isFinite(Number(toGo))
      && Number(toGo) > descentReachNm(alt) && alt >= 5000;

    if (lastPhase === "CLIMB"    && stayClimb)    return "CLIMB";
    if ((lastPhase === "DESCENT" || lastPhase === "APPROACH") && !enterClimb) {
      if (farOut) return "CRUISE";   // a step down, already taken for the descent
      return lastPhase === "APPROACH" || alt < 5000 ? "APPROACH" : "DESCENT";
    }

    if (enterClimb)   return "CLIMB";
    if (enterDescent) {
      if (farOut) return "CRUISE";   // a step down in the cruise
      return alt < 5000 ? "APPROACH" : "DESCENT";
    }
    // Level under 5000 ft is the approach only on the way down. On the way up
    // it is a departure level-off (3000 ft until cleared higher), which this
    // used to call APPROACH and play the approach announcement for.
    if (alt < 5000)   return lastPhase === "CLIMB" ? "CLIMB" : "APPROACH";
    // A level-off out of the climb is cruise only at the cruise level. Coming
    // from anywhere else (cruise itself, or no history after a restart) a
    // level aircraft above 5000 ft is cruising, as before.
    if ((lastPhase === "CLIMB" || lastPhase === "TAKEOFF") && !atCruise(alt, ctx)) return "CLIMB";
    return "CRUISE";
  }

  // Pushed back but not yet airborne. Airborne before OFF needs the sim to say
  // so AND takeoff speed: a pushback tug can lift the nose gear off the ground
  // flag for a moment, and an unknown flag is just unknown.
  if (time_out) {
    if (onGnd === false && spd >= 40) return "CLIMB";   // OFF not fired yet
    if (spd >= 40) return "TAKEOFF";
    return "TAXI OUT";
  }

  // Still at the gate, no OUT event yet.
  return "BOARDING";
};

/**
 * A flight's phase from the one snapshot the live board has: the same rules as
 * Live Tracking, without its history. The board used to look only at which
 * block times were set, so every flight read BOARDING, TAXIING or IN FLIGHT
 * from pushback to touchdown (2026-10-08). Altitude, speed and vertical speed
 * are already in /live/flights; with no history, a level-off in the climb
 * reads CRUISE and the flight that never reported a ground flag is judged on
 * speed, which is the board's job: a glance, not an announcement trigger.
 */
export const BOARD_PHASES = ["BOARDING", "TAXI OUT", "TAKEOFF", "CLIMB", "CRUISE",
  "DESCENT", "APPROACH", "TAXI IN", "ARRIVED"];
export const boardPhase = (f = {}) => phaseFromTelemetry(
  { alt: Number(f.alt) || 0, spd: Number(f.spd) || 0, vs: Number(f.vs) || 0, on_ground: null },
  f, null);

/** Placeholder shown while the profile card is loading.
 *
 * This used to be a hardcoded rank ladder — Cadet / Second Officer / First
 * Officer / ... — which was NOT VACanada's. Seven of the eight names were
 * wrong: rank 7 is "Senior Captain", not "Chief Pilot"; rank 5 is "Wide-body
 * First Officer", not "Captain"; rank 0 was missing entirely. Measured against
 * the real ladder, 205 of 298 pilots would have seen a bogus rank whenever
 * /pilot/profile-card failed to load.
 *
 * The ranks table (migration 002, generated from settings.php) is the only
 * authority. A fallback that invents different names is worse than no fallback,
 * so this returns a neutral placeholder and nothing else.
 */
export const rankTitle = () => "—";

/** Long-form UTC date shown in the pilot header. e.g. "July 14th 2026, 21:21Z" */
export const fmtSystemDate = (d = new Date()) => {
  const months = ["January","February","March","April","May","June","July",
                  "August","September","October","November","December"];
  const day = d.getUTCDate();
  const suffix = (day % 10 === 1 && day !== 11) ? "st" :
                 (day % 10 === 2 && day !== 12) ? "nd" :
                 (day % 10 === 3 && day !== 13) ? "rd" : "th";
  const hh = String(d.getUTCHours()).padStart(2, "0");
  const mm = String(d.getUTCMinutes()).padStart(2, "0");
  return `${months[d.getUTCMonth()]} ${day}${suffix} ${d.getUTCFullYear()}, ${hh}:${mm}Z`;
};

/**
 * The colour family of a phase chip (index.css .phase-pill), matching the
 * dispatcher dashboard: on the ground grey, taxiing amber, climbing or
 * descending blue, cruise red, arrived green.
 */
export const phaseTone = (phase) => {
  switch (phase) {
    case "TAXI OUT": case "TAXI IN": case "TAKEOFF": return "phase-taxi";
    case "CLIMB": case "DESCENT": case "APPROACH":  return "phase-air";
    case "CRUISE":                                   return "phase-cruise";
    case "ARRIVED":                                  return "phase-in";
    default:                                         return "";
  }
};
